import React, { useState, useEffect } from 'react';
import { Badge, IconButton, Menu, MenuItem, Typography, Box } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { fetchNotifications, markNotificationAsRead, fetchUserNames } from '../utils/notifications';

const Notifications = ({ session }) => {
  const [notifications, setNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (session) {
      loadNotifications();
    }
  }, [session]);

  const loadNotifications = async () => {
    try {
      const fetchedNotifications = await fetchNotifications(session.user.id);
      
      const userIds = [...new Set(fetchedNotifications.map(n => n.user_id))];
      const userNames = await fetchUserNames(userIds);
      
      const notificationsWithNames = fetchedNotifications.map(n => ({
        ...n,
        user_name: userNames[n.user_id] || 'Bilinmeyen Kullanıcı'
      }));

      setNotifications(notificationsWithNames);
    } catch (error) {
      console.error('Bildirimler yüklenirken hata:', error);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    try {
      await markNotificationAsRead(notification.id);
      await loadNotifications();
      handleClose();
    } catch (error) {
      console.error('Bildirim işlenirken hata:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <>
      <IconButton 
        onClick={handleClick}
        sx={{
          position: 'relative',
          transition: 'transform 0.2s ease',
          '&:hover': {
            transform: 'scale(1.1)',
          },
          color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
        }}
      >
        <Badge 
          badgeContent={unreadCount} 
          color="primary"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: theme => theme.palette.primary.main,
              color: '#fff',
              fontWeight: 600,
              minWidth: '20px',
              height: '20px',
              padding: '0 6px',
              fontSize: '0.75rem',
              transform: 'scale(1) translate(25%, -25%)',
            }
          }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: '280px',
            maxWidth: '320px',
            mt: 1,
            marginRight: '16px',
            borderRadius: '12px',
            border: theme => `1px solid ${theme.palette.divider}`,
            background: theme => `radial-gradient(circle at 50% 50%, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
            backdropFilter: 'blur(8px)',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: theme => `
                radial-gradient(circle at center, ${theme.palette.primary.main}15 0%, transparent 50%),
                repeating-linear-gradient(45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 2px, transparent 2px, transparent 10px),
                repeating-linear-gradient(-45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 2px, transparent 2px, transparent 10px)
              `,
              backgroundSize: '100% 100%, 20px 20px, 20px 20px',
              animation: 'gradientMove 20s ease-in-out infinite',
              zIndex: -1,
              borderRadius: 'inherit',
              opacity: 0.5,
            },
            boxShadow: theme => `0 8px 32px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.15)'}`,
          }
        }}
      >
        <Box
          sx={{
            py: 1,
            px: 2,
            borderBottom: theme => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
            }}
          >
            Bildirimler
          </Typography>
        </Box>
        {notifications.length === 0 ? (
          <MenuItem sx={{ py: 2 }}>
            <Typography 
              variant="body2"
              sx={{
                color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                textAlign: 'center',
                width: '100%',
              }}
            >
              Bildirim bulunmuyor
            </Typography>
          </MenuItem>
        ) : (
          notifications.map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                py: 1.5,
                px: 2,
                borderLeft: theme => notification.read ? 'none' : `3px solid ${theme.palette.primary.main}`,
                backgroundColor: theme => notification.read 
                  ? 'transparent'
                  : theme.palette.mode === 'dark' 
                    ? 'rgba(138, 43, 226, 0.08)'
                    : 'rgba(138, 43, 226, 0.04)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: theme => theme.palette.mode === 'dark'
                    ? 'rgba(138, 43, 226, 0.15)'
                    : 'rgba(138, 43, 226, 0.08)',
                  transform: 'translateX(4px)',
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme => theme.palette.mode === 'dark' 
                      ? notification.read ? 'rgba(255, 255, 255, 0.7)' : '#fff'
                      : notification.read ? 'rgba(0, 0, 0, 0.7)' : '#000',
                    fontWeight: notification.read ? 400 : 600,
                  }}
                >
                  {notification.message}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme => theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.5)'
                      : 'rgba(0, 0, 0, 0.5)',
                  }}
                >
                  {new Date(notification.created_at).toLocaleString('tr-TR')}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

export default Notifications;
